import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import WishlistIcon from './WishlistIcon';
import CardQuantityAdjuster from './CardQuantityAdjuster';
import slugService from '../services/slugService';
import { getColorFromString } from '../utils/colorGenerator';
import { Card } from 'shared/models';
import './CardItem.css';
import TradeIcon from './TradeIcon';

// Lazy load CameraAction to reduce bundle size for non-logged in users
const CameraAction = lazy(() => import('./CameraAction'));

/**
 * A reusable card component that displays card information and actions
 * 
 * @param {Object} props
 * @param {Card} props.card - The Card instance containing details with inWishlist and quantity properties
 * @param {boolean} props.compact - Whether to show the card in compact mode (default: false)
 * @param {boolean} props.showImage - Whether to show the card image (default: false)
 * @param {number} props.index - Index for animation delay
 * @param {string} props.cardUrl - The URL to navigate to when clicking the card (if not provided, will be generated using slugService)
 * @param {Object} props.insertSet - The insert set object (optional, used for URL generation if cardUrl not provided)
 * @param {Object} props.product - The product object (optional, used for URL generation if cardUrl not provided)
 */
const CardItem = ({
  card,
  compact = false,
  showImage = false,
  index = 0,
  cardUrl,
  insertSet,
  product
}) => {
  // Set background color for grid style only
  const backgroundColor = !card.imageUrl ? getColorFromString(card.id) : undefined;

  // Use provided cardUrl or generate one if not provided
  const linkUrl = cardUrl || slugService.getCardUrl(card, insertSet, product);
  
  return (
    <div 
      className={`card-item grid-style ${compact ? 'compact' : ''}`}
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      <div className="card-item-main">
        {/* Clickable image area */}
        <Link to={linkUrl} className="card-item-image-link">
          <div className="card-item-image" style={{ backgroundColor }}>
            {card.imageUrl ? (
              <img src={card.imageUrl} alt={card.name} />
            ) : (
              <div className="card-image-placeholder">
                {card.cardNumber || card.name.charAt(0)}
              </div>
            )}
          </div>
        </Link>
        
        {/* Card number badge at top right of image */}
        {card.cardNumber && (
          <div className="card-number-badge">
            <span>#{card.cardNumber}</span>
          </div>
        )}
        
        <div className="card-item-content">
          <div className="card-item-header">
            {/* Clickable card name */}
            <Link to={linkUrl} className="card-item-name">
              {card.name}
            </Link>
          </div>
          
          <div className="card-item-details">
            <div className="card-item-actions-left">
              <WishlistIcon
                card={card}
                size={24}
              />
              <TradeIcon
                card={card}
                size={24}
              />
              <Suspense fallback={null}>
                <CameraAction
                  card={card}
                  size={24}
                />
              </Suspense>
              
              {card.serialNumber && (
                <div className="card-item-serial">
                  <span>/{card.serialNumber}</span>
                </div>
              )}
            </div>
            
            <div className="card-item-actions">
              <CardQuantityAdjuster
                card={card}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

CardItem.propTypes = {
  card: PropTypes.instanceOf(Card).isRequired,
  compact: PropTypes.bool,
  showImage: PropTypes.bool,
  index: PropTypes.number,
  cardUrl: PropTypes.string,
  insertSet: PropTypes.object,
  product: PropTypes.object
};

export default CardItem;